/**
 * usePresenceSocket Hook
 *
 * React hook for managing presence socket connection lifecycle.
 * Handles real-time online player list updates via Socket.IO.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { PresenceSocket, getSharedPresenceSocket, releaseSharedPresenceSocket } from '../services/presence-socket';
import type { OnlinePlayer, ActivityStatus } from '@drawn-of-war/shared';

/**
 * Hook return type
 */
export interface UsePresenceSocketResult {
  /** Array of online players */
  players: OnlinePlayer[];
  /** Whether socket is currently connected */
  isConnected: boolean;
  /** Error message if connection failed */
  error: string | null;
  /** Total online player count */
  totalOnline: number;
  /** Set hidden status (privacy toggle) */
  setHidden: (hidden: boolean) => void;
  /** Manually refresh the player list */
  refresh: () => void;
}

/**
 * usePresenceSocket Hook
 *
 * Manages Socket.IO connection to presence namespace and maintains
 * real-time online player list state.
 *
 * Features:
 * - Auto-connects on mount
 * - Auto-disconnects on unmount
 * - Subscribes to presence updates
 * - Maintains synchronized player list
 * - Automatic heartbeat
 * - Tracks connection state
 *
 * @returns Object with players array, connection status, and utilities
 */
export function usePresenceSocket(): UsePresenceSocketResult {
  const [players, setPlayers] = useState<OnlinePlayer[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const socketRef = useRef<PresenceSocket | null>(null);

  // Set hidden status
  const setHidden = useCallback((hidden: boolean) => {
    if (socketRef.current) {
      socketRef.current.setHidden(hidden);
    }
  }, []);

  // Manually refresh player list
  const refresh = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.subscribe();
    }
  }, []);

  useEffect(() => {
    try {
      const socket = getSharedPresenceSocket();
      socketRef.current = socket;

      // Full online list update
      socket.onOnlineList((newPlayers: OnlinePlayer[]) => {
        setPlayers(newPlayers);
        setIsConnected(socket.isConnected());
      });

      // User came online
      socket.onUserOnline((data: { id: string; displayName: string; activity: ActivityStatus }) => {
        setPlayers((prev) => {
          // Avoid duplicates
          const exists = prev.some(p => p.id === data.id);
          if (exists) {
            return prev;
          }
          return [...prev, {
            id: data.id,
            displayName: data.displayName,
            activity: data.activity,
            stats: { wins: 0, losses: 0 }
          }];
        });
        setIsConnected(socket.isConnected());
      });

      // User went offline
      socket.onUserOffline((data: { id: string }) => {
        setPlayers((prev) => prev.filter(p => p.id !== data.id));
        setIsConnected(socket.isConnected());
      });

      // User activity changed
      socket.onActivityChanged((data: { id: string; activity: ActivityStatus }) => {
        setPlayers((prev) =>
          prev.map(p =>
            p.id === data.id ? { ...p, activity: data.activity } : p
          )
        );
        setIsConnected(socket.isConnected());
      });

      // User might be offline (temporary state)
      socket.onUserMaybeOffline((data: { id: string }) => {
        // Could show a "connecting..." state, but for now we just wait
        // for the cleanup service to confirm offline
        console.log(`[usePresenceSocket] User ${data.id} might be offline`);
      });

      // Cleanup on unmount — release shared ref, don't disconnect directly
      return () => {
        socketRef.current = null;
        releaseSharedPresenceSocket();
      };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      console.error('[usePresenceSocket] Failed to connect:', err);
    }
  }, []);

  return {
    players,
    isConnected,
    error,
    totalOnline: players.length,
    setHidden,
    refresh
  };
}
